import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from "recharts";

interface MonthlyDataPoint {
  month: string;     // "YYYY-MM"
  websites: number;  // new websites in that month
  users: number;     // new users in that month
}

interface MonthlyTrendsChartProps {
  data: MonthlyDataPoint[];
  t: (key: string) => string;
  lang: string;
  websitesLineColor?: string;
  usersLineColor?: string;
}

export function MonthlyTrendsChart({
  data,
  t,
  lang,
  websitesLineColor,
  usersLineColor,
}: MonthlyTrendsChartProps) {
  const isGerman = (lang || "").toLowerCase().startsWith("de");
  const numberLocale = isGerman ? "de-DE" : "en-US";
  const numberFormatter = new Intl.NumberFormat(numberLocale);

  const labelWebsites = t("statistics.newWebsites");
  const labelUsers = t("statistics.newUsers");
  const solidHint = isGerman ? "durchgezogen" : "solid";
  const dashedHint = isGerman ? "gestrichelt" : "dashed";
  const axisWebsitesLabel = `${labelWebsites} (${solidHint})`;
  const axisUsersLabel = `${labelUsers} (${dashedHint})`;
  const websitesLegendLabel = `${labelWebsites} (${solidHint})`;
  const usersLegendLabel = `${labelUsers} (${dashedHint})`;

  const formatMonthTick = (m: string) => {
    const parts = m.split("-");
    if (parts.length !== 2) return m;

    const yyyy = parts[0];
    const mm = Number(parts[1]);
    if (!Number.isFinite(mm) || mm < 1 || mm > 12) return m;

    if (isGerman) {
      return `${String(mm).padStart(2, "0")}.${yyyy}`; // "01.2026"
    }

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[mm - 1]} ${yyyy}`;
  };

  // Log scale cannot render zero, so clamp non-positive values to the axis floor.
  // Tooltips still use the raw values.
  const websitesLogFloor = 1;
  const chartData = (data ?? []).map((d) => ({
    month: d.month,
    websitesRaw: d.websites,
    websitesPlot: d.websites > 0 ? d.websites : websitesLogFloor,
    users: d.users,
  }));
  const maxWebsitesRaw = chartData.reduce(
    (max, point) => Math.max(max, point.websitesRaw),
    0
  );
  const websitesAxisMax =
    maxWebsitesRaw <= 1 ? 10 : 10 ** Math.ceil(Math.log10(maxWebsitesRaw));
  const websitesTicks = Array.from(
    { length: Math.max(2, Math.ceil(Math.log10(websitesAxisMax)) + 1) },
    (_, index) => 10 ** index
  );
  const resolvedWebsitesLineColor = websitesLineColor ?? "var(--category-scale-3)";
  const resolvedUsersLineColor = usersLineColor ?? "var(--category-scale-4)";

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={250}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 30, left: 26, bottom: 0 }}
        >
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />

          <XAxis dataKey="month" tickFormatter={formatMonthTick} tickMargin={8} />
          <YAxis
            yAxisId="websites"
            type="number"
            scale="log"
            domain={[websitesLogFloor, websitesAxisMax]}
            ticks={websitesTicks}
            allowDecimals={false}
            width={94}
            tickMargin={8}
            tickFormatter={(value: number) => numberFormatter.format(value)}
            tick={{ fill: resolvedWebsitesLineColor }}
            axisLine={{ stroke: resolvedWebsitesLineColor }}
            label={{
              value: axisWebsitesLabel,
              angle: -90,
              position: "left",
              offset: 12,
              fill: resolvedWebsitesLineColor,
            }}
          />
          <YAxis
            yAxisId="users"
            orientation="right"
            type="number"
            allowDecimals={false}
            width={56}
            tickMargin={8}
            tickFormatter={(value: number) => numberFormatter.format(value)}
            tick={{ fill: resolvedUsersLineColor }}
            axisLine={{ stroke: resolvedUsersLineColor }}
            label={{
              value: axisUsersLabel,
              angle: 90,
              position: "right",
              offset: 8,
              fill: resolvedUsersLineColor,
            }}
          />

          <Tooltip
            formatter={(value, name, item) => {
              const seriesName = name === "websitesPlot" ? labelWebsites : labelUsers;
              if (name === "websitesPlot") {
                const raw = Number(
                  (item && "payload" in item && item.payload?.websitesRaw) || 0
                );
                return [numberFormatter.format(raw), seriesName];
              }
              return [numberFormatter.format(Number(value ?? 0)), seriesName];
            }}
            labelFormatter={(label) => `${t("statistics.monthLabel")}: ${formatMonthTick(String(label))}`}
            cursor={{ fill: "var(--muted)" }}
            contentStyle={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--foreground)",
              borderRadius: 8,
            }}
          />

          <Legend
            formatter={(value) =>
              value === "websitesPlot" ? websitesLegendLabel : usersLegendLabel
            }
          />

          <Line
            dataKey="websitesPlot"
            name="websitesPlot"
            yAxisId="websites"
            stroke={resolvedWebsitesLineColor}
            strokeWidth={2}
            strokeDasharray="0"
            dot={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="users"
            name="users"
            yAxisId="users"
            stroke={resolvedUsersLineColor}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
